<template>
  <div class="auth-panel">
    <div class="card">
      <!-- Bundled locally rather than hotlinked from the WordPress uploads
           folder — that site is being decommissioned in favour of Webflow. -->
      <img
        class="logo"
        :src="logoSrc"
        alt="DegreeData"
      />
      <h1 class="heading">
        <template v-if="activeTab === 'signup'">
          <template v-if="isInvite">
            Join your <em class="grad-text">team</em>
          </template>
          <template v-else-if="isPurchase">
            Create your account to <em class="grad-text">subscribe</em>
          </template>
          <template v-else>
            Start your <em class="grad-text">30-day trial</em>
          </template>
        </template>
        <template v-else-if="activeTab === 'login'">
          Welcome <em class="grad-text">back</em>.
        </template>
        <template v-else>Reset your password</template>
      </h1>
      <p class="subheading">
        <template v-if="activeTab === 'signup'">
          <template v-if="isInvite">
            You've been invited to {{ appName }}. Create your account to join.
          </template>
          <template v-else-if="isPurchase">
            Next step is secure checkout for the {{ tierLabel }} plan.
          </template>
          <template v-else>
            No credit card. Full access to {{ appName }}. Cancel anytime.
          </template>
        </template>
        <template v-else-if="activeTab === 'login'">
          <template v-if="isPurchase">
            Log in and we'll take you straight to checkout.
          </template>
          <template v-else>Welcome back. Pick up where you left off.</template>
        </template>
        <template v-else>
          Enter your email and we'll send you a link to set a new password.
        </template>
      </p>

      <nav class="tabs" role="tablist">
        <button
          v-for="tab in tabs"
          :key="tab.id"
          type="button"
          role="tab"
          :aria-selected="activeTab === tab.id"
          class="tab"
          :class="{ 'tab--active': activeTab === tab.id }"
          @click="switchTab(tab.id)"
        >
          {{ tab.label }}
        </button>
      </nav>

      <form
        v-if="activeTab === 'signup'"
        class="form"
        @submit.prevent="onSignup"
        novalidate
      >
        <label class="field">
          <span class="field-label">Work email</span>
          <span class="input-wrap">
            <svg class="input-icon" viewBox="0 0 24 24" aria-hidden="true">
              <rect x="3" y="5" width="18" height="14" rx="2" />
              <path d="M3 7l9 6 9-6" />
            </svg>
            <input
              v-model="form.email"
              type="email"
              autocomplete="email"
              placeholder="alex@college.edu"
              required
              :disabled="submitting"
            />
          </span>
        </label>

        <label class="field">
          <span class="field-label">Password</span>
          <span class="input-wrap">
            <svg class="input-icon" viewBox="0 0 24 24" aria-hidden="true">
              <rect x="4" y="11" width="16" height="10" rx="2" />
              <path d="M8 11V8a4 4 0 0 1 8 0v3" />
            </svg>
            <input
              v-model="form.password"
              :type="showPassword ? 'text' : 'password'"
              autocomplete="new-password"
              placeholder="Create a password"
              minlength="8"
              required
              :disabled="submitting"
            />
            <button
              type="button"
              class="reveal"
              @click="showPassword = !showPassword"
              :aria-label="showPassword ? 'Hide password' : 'Show password'"
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
            </button>
          </span>
          <ul class="pw-rules" aria-label="Password requirements">
            <li
              v-for="rule in passwordRules"
              :key="rule.label"
              :class="{ 'pw-rules__item--ok': rule.ok }"
            >
              <span class="pw-rules__dot">
                <svg
                  v-if="rule.ok"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                  class="pw-rules__check"
                >
                  <path d="M4 12.5l5 5 11-12" />
                </svg>
              </span>
              {{ rule.label }}
            </li>
          </ul>
        </label>

        <label class="field">
          <span class="field-label">Confirm password</span>
          <span class="input-wrap">
            <svg class="input-icon" viewBox="0 0 24 24" aria-hidden="true">
              <rect x="4" y="11" width="16" height="10" rx="2" />
              <path d="M8 11V8a4 4 0 0 1 8 0v3" />
            </svg>
            <input
              v-model="form.confirm"
              type="password"
              autocomplete="new-password"
              placeholder="Confirm your password"
              required
              :disabled="submitting"
            />
          </span>
        </label>

        <p v-if="errorMessage" class="error" role="alert">
          {{ errorMessage }}
        </p>

        <button type="submit" class="primary" :disabled="submitting">
          <template v-if="submitting">
            {{ signupPendingLabel }}
          </template>
          <template v-else>
            {{ signupLabel }}
            <svg viewBox="0 0 24 24" aria-hidden="true" class="primary__arrow">
              <path d="M5 12h14M13 6l6 6-6 6" />
            </svg>
          </template>
        </button>

        <p class="fineprint">
          By continuing you agree to our
          <a
            href="https://degreedata.com/terms/"
            target="_blank"
            rel="noopener"
            >Terms</a
          >
          and
          <a
            href="https://degreedata.com/privacy-policy/"
            target="_blank"
            rel="noopener"
            >Privacy&nbsp;Policy</a
          >. We'll only use your email for product updates.
        </p>
      </form>

      <form
        v-else-if="activeTab === 'login'"
        class="form"
        @submit.prevent="onLogin"
        novalidate
      >
        <label class="field">
          <span class="field-label">Email</span>
          <span class="input-wrap">
            <svg class="input-icon" viewBox="0 0 24 24" aria-hidden="true">
              <rect x="3" y="5" width="18" height="14" rx="2" />
              <path d="M3 7l9 6 9-6" />
            </svg>
            <input
              v-model="form.email"
              type="email"
              autocomplete="email"
              placeholder="you@college.edu"
              required
              :disabled="submitting"
            />
          </span>
        </label>

        <label class="field">
          <span class="field-label">Password</span>
          <span class="input-wrap">
            <svg class="input-icon" viewBox="0 0 24 24" aria-hidden="true">
              <rect x="4" y="11" width="16" height="10" rx="2" />
              <path d="M8 11V8a4 4 0 0 1 8 0v3" />
            </svg>
            <input
              v-model="form.password"
              :type="showPassword ? 'text' : 'password'"
              autocomplete="current-password"
              placeholder="Your password"
              required
              :disabled="submitting"
            />
            <button
              type="button"
              class="reveal"
              @click="showPassword = !showPassword"
              :aria-label="showPassword ? 'Hide password' : 'Show password'"
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
            </button>
          </span>
        </label>

        <p v-if="errorMessage" class="error" role="alert">
          {{ errorMessage }}
        </p>

        <button type="submit" class="primary" :disabled="submitting">
          <template v-if="submitting">Signing in…</template>
          <template v-else>
            Log in
            <svg viewBox="0 0 24 24" aria-hidden="true" class="primary__arrow">
              <path d="M5 12h14M13 6l6 6-6 6" />
            </svg>
          </template>
        </button>

        <p class="fineprint center">
          <button type="button" class="textlink" @click="switchTab('forgot')">
            Forgot your password?
          </button>
        </p>
      </form>

      <form
        v-else-if="activeTab === 'forgot'"
        class="form"
        @submit.prevent="onForgot"
        novalidate
      >
        <p v-if="resetSent" class="success">
          Check your inbox — if an account exists for
          <strong>{{ form.email }}</strong
          >, we just sent a password reset link.
        </p>
        <template v-else>
          <label class="field">
            <span class="field-label">Email</span>
            <span class="input-wrap">
              <svg class="input-icon" viewBox="0 0 24 24" aria-hidden="true">
                <rect x="3" y="5" width="18" height="14" rx="2" />
                <path d="M3 7l9 6 9-6" />
              </svg>
              <input
                v-model="form.email"
                type="email"
                autocomplete="email"
                placeholder="you@college.edu"
                required
                :disabled="submitting"
              />
            </span>
          </label>

          <p v-if="errorMessage" class="error" role="alert">
            {{ errorMessage }}
          </p>

          <button type="submit" class="primary" :disabled="submitting">
            <template v-if="submitting">Sending…</template>
            <template v-else>Send reset link</template>
          </button>
        </template>
      </form>
    </div>
  </div>
</template>

<script>
import {
  signupWithTrial,
  signupTeamMember,
  joinTeamWithToken,
  login,
  sendResetEmail,
} from "./client";
import logoSrc from "@/assets/degreedata-logo.png";

export default {
  name: "AuthPanel",
  props: {
    initialTab: {
      type: String,
      default: "signup",
      validator: (v) => ["signup", "login", "forgot"].includes(v),
    },
    appName: {
      type: String,
      required: true,
    },
    // When present, the panel switches to "join a team" mode: signup/login
    // attach the member to the inviting team instead of starting a trial.
    inviteToken: {
      type: String,
      default: "",
    },
    // Tier slug from a "Buy now" link on the marketing site ("single" | "two" |
    // "three"). Only reframes the copy — the caller runs the checkout once this
    // panel emits `authorized`. Signup still stamps the 30-day trial, which is
    // deliberate: an abandoned checkout leaves a usable account behind.
    purchaseTier: {
      type: String,
      default: "",
    },
  },
  emits: ["authorized", "trial-started"],
  data() {
    return {
      logoSrc,
      activeTab: this.initialTab,
      tabs: [
        { id: "signup", label: "Sign Up" },
        { id: "login", label: "Log In" },
        { id: "forgot", label: "Forgot" },
      ],
      form: { email: "", password: "", confirm: "" },
      showPassword: false,
      submitting: false,
      errorMessage: "",
      resetSent: false,
    };
  },
  computed: {
    isPurchase() {
      // An invite is always free to the invitee, so it outranks a stray tier.
      return !!this.purchaseTier && !this.inviteToken;
    },
    signupLabel() {
      if (this.isInvite) return "Join the team";
      // A buyer is one step from Stripe, so the button must not promise a trial.
      return this.isPurchase ? "Continue to checkout" : "Start my 30-day trial";
    },
    signupPendingLabel() {
      if (this.isInvite) return "Joining…";
      return this.isPurchase
        ? "Setting up your account…"
        : "Setting up your trial…";
    },
    tierLabel() {
      return (
        { single: "single user", two: "two user", three: "three user", test: "$5 test" }[
          this.purchaseTier
        ] || "subscription"
      );
    },
    isInvite() {
      return !!this.inviteToken;
    },
    passwordRules() {
      const pw = this.form.password;
      return [
        { label: "8+ characters", ok: pw.length >= 8 },
        { label: "One uppercase", ok: /[A-Z]/.test(pw) },
        { label: "One number", ok: /[0-9]/.test(pw) },
        { label: "One symbol", ok: /[^A-Za-z0-9]/.test(pw) },
      ];
    },
    passwordOk() {
      return this.passwordRules.every((r) => r.ok);
    },
  },
  methods: {
    switchTab(id) {
      this.activeTab = id;
      this.errorMessage = "";
      this.resetSent = false;
    },
    formatError(error) {
      return (
        error?.response?.data?.message ||
        error?.message ||
        "Something went wrong. Please try again."
      );
    },
    async onSignup() {
      this.errorMessage = "";
      // The form is `novalidate`, so required fields are checked here rather
      // than by the browser.
      if (!this.passwordOk) {
        this.errorMessage =
          "Please pick a password that meets all the requirements.";
        return;
      }
      if (this.form.password !== this.form.confirm) {
        this.errorMessage = "Passwords do not match.";
        return;
      }
      this.submitting = true;
      try {
        if (this.isInvite) {
          await signupTeamMember({
            email: this.form.email.trim(),
            password: this.form.password,
            inviteToken: this.inviteToken,
          });
        } else {
          await signupWithTrial({
            email: this.form.email.trim(),
            password: this.form.password,
          });
          // A brand-new trial account, as opposed to a login or a team invite.
          // The host app decides what to do with it (ad conversion pixel).
          this.$emit("trial-started");
        }
        this.$emit("authorized");
      } catch (error) {
        this.errorMessage = this.formatError(error);
      } finally {
        this.submitting = false;
      }
    },
    async onLogin() {
      this.errorMessage = "";
      this.submitting = true;
      try {
        await login({
          email: this.form.email.trim(),
          password: this.form.password,
        });
        if (this.isInvite) {
          await joinTeamWithToken(this.inviteToken);
        }
        this.$emit("authorized");
      } catch (error) {
        this.errorMessage = this.formatError(error);
      } finally {
        this.submitting = false;
      }
    },
    async onForgot() {
      this.errorMessage = "";
      this.submitting = true;
      try {
        await sendResetEmail({ email: this.form.email.trim() });
        this.resetSent = true;
      } catch (error) {
        this.errorMessage = this.formatError(error);
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style scoped>
.auth-panel {
  width: 100%;
  max-width: 480px;
  margin: 0 auto;
  color: var(--ink);
  font-family: var(--font-sans);
  text-align: left;
}

.card {
  position: relative;
  background: var(--paper);
  border: 1px solid var(--line);
  border-radius: 16px;
  padding: 40px 40px 32px;
  box-shadow:
    0 1px 2px rgba(20, 18, 40, 0.04),
    0 12px 40px -24px rgba(30, 20, 60, 0.18);
}

.logo {
  height: 32px;
  width: auto;
  display: block;
  margin: 0 0 24px;
}

.heading {
  font-family: var(--font-serif);
  font-size: 34px;
  line-height: 1.2;
  letter-spacing: -0.015em;
  color: var(--ink);
  font-weight: 400;
  margin: 0;
}

.heading em {
  font-style: italic;
}

.grad-text {
  background: var(--grad);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
}

.subheading {
  font-size: 14.5px;
  color: var(--mute);
  margin: 12px 0 22px;
  line-height: 1.55;
}

.tabs {
  display: flex;
  gap: 4px;
  border-bottom: 1px solid var(--line);
  margin-bottom: 24px;
}

.tab {
  flex: 1;
  background: transparent;
  border: none;
  padding: 10px 0;
  font-family: inherit;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--mute);
  cursor: pointer;
  border-bottom: 2px solid transparent;
  margin-bottom: -1px;
  transition: color 0.15s ease, border-color 0.15s ease;
}

.tab:hover {
  color: var(--ink-soft);
}

.tab--active {
  color: var(--ink);
  border-bottom-color: var(--ink);
}

.form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.field {
  display: block;
}

.field-label {
  display: block;
  font-size: 12.5px;
  font-weight: 500;
  color: var(--ink-soft);
  margin-bottom: 6px;
}

.input-wrap {
  position: relative;
  display: flex;
  align-items: center;
  background: var(--paper);
  border: 1px solid var(--hair);
  border-radius: 10px;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.input-wrap:focus-within {
  border-color: var(--ink);
  box-shadow: 0 0 0 4px rgba(30, 20, 60, 0.06);
}

.input-icon {
  width: 16px;
  height: 16px;
  margin-left: 14px;
  color: var(--mute);
  flex-shrink: 0;
  fill: none;
  stroke: currentColor;
  stroke-width: 1.6;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.input-wrap input {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  padding: 13px 14px;
  font-size: 14.5px;
  font-family: inherit;
  color: var(--ink);
}

.input-wrap input::placeholder {
  color: var(--faint);
}

.input-wrap input:disabled {
  opacity: 0.6;
}

.reveal {
  background: transparent;
  border: none;
  padding: 10px 14px;
  color: var(--mute);
  cursor: pointer;
  display: flex;
  align-items: center;
}

.reveal:hover {
  color: var(--ink-soft);
}

.reveal svg {
  width: 16px;
  height: 16px;
  fill: none;
  stroke: currentColor;
  stroke-width: 1.6;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.pw-rules {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px 12px;
  list-style: none;
  padding: 0;
  margin: 10px 0 0;
  font-size: 12px;
  color: var(--mute);
}

.pw-rules li {
  display: flex;
  align-items: center;
  gap: 6px;
  transition: color 0.15s ease;
}

.pw-rules__item--ok {
  color: var(--ink);
}

.pw-rules__dot {
  width: 14px;
  height: 14px;
  border-radius: 999px;
  border: 1px solid var(--hair);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background 0.15s ease, border-color 0.15s ease;
}

.pw-rules__item--ok .pw-rules__dot {
  background: var(--ink);
  border-color: var(--ink);
}

.pw-rules__check {
  width: 9px;
  height: 9px;
  fill: none;
  stroke: #fff;
  stroke-width: 3;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.primary {
  width: 100%;
  padding: 14px 22px;
  border-radius: 10px;
  border: none;
  background: var(--ink);
  color: #fff;
  font-family: inherit;
  font-size: 14.5px;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 4px;
  transition: background 0.15s ease, transform 0.15s ease;
}

.primary:not(:disabled):hover {
  background: #000;
}

.primary:not(:disabled):active {
  transform: translateY(1px);
}

.primary:disabled {
  background: var(--faint);
  cursor: not-allowed;
}

.primary__arrow {
  width: 16px;
  height: 16px;
  fill: none;
  stroke: #fff;
  stroke-width: 1.8;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.fineprint {
  font-size: 12px;
  color: var(--mute);
  line-height: 1.55;
  margin: 4px 0 0;
}

.fineprint.center {
  text-align: center;
}

.fineprint a {
  color: var(--ink);
  text-decoration: underline;
}

.textlink {
  background: none;
  border: none;
  padding: 0;
  color: var(--ink);
  text-decoration: underline;
  cursor: pointer;
  font-family: inherit;
  font-size: inherit;
}

.textlink:hover {
  color: var(--ink-soft);
}

.error {
  font-size: 13px;
  color: var(--error);
  margin: 0;
}

.success {
  font-size: 14px;
  color: var(--ink);
  line-height: 1.55;
  margin: 0 0 4px;
}

@media (max-width: 540px) {
  .card {
    padding: 32px 24px 24px;
    border-radius: 14px;
  }
  .heading {
    font-size: 28px;
  }
  .logo {
    height: 28px;
  }
}
</style>
